import { html, nothing, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';

import NSJobComponent from '../../../job.js';
import type Job from '../../../../../models/job.js';
import type Person from '../../../../../models/person.js';
import { createLogger } from '../../../../../ns-services/logger.js';
import { claimJob, isUnclaimed } from '../../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../../helpers/public-ledger-balance-api.js';
import { personDisplayName } from '../../../../../helpers/person.js';
import type { AutoPaymentRow } from '../../../../../helpers/auto-payments.js';
import { autopayCardAction } from '../../../../../helpers/auto-payments-api.js';
import { CHANGE_WAITING_TEXT, CHILD_NODE } from '../../../../../helpers/auto-payment-changes.js';
import {
  currentChildWorkflow,
  jobFeedPath,
  waitForChildWorkflow,
  waitForOpenJobPath,
} from '../../../../../helpers/workflow-job-open.js';
import { viewPath } from '../../../../../helpers/route.js';

const logger = createLogger('ns-public-ledger-auto-payments-card');

/**
 * What the automatic payments cards of #25 phase 4 share (Payment failed, Limit reached, Change target, Re-sign allowance,
 * and from phase 6 Raise operating target and the change cards).
 * Each is claimed from the company's public ledger signatories team, reads the company's `plAutoPayment` row (signatories
 * may read it) and its own job metadata, and offers its actions as its own buttons. Every outcome is the server's: the
 * lambda that checked the action completes the job a moment later, and the card closes itself when the job's update
 * arrives (or straight away if it already has).
 */
export default class NSAutoPaymentsCard extends NSJobComponent {
  @state()
  protected _row: AutoPaymentRow | null = null;

  @state()
  protected _busy = false;

  // What the card is waiting for after an action the server completes it on, shown until the job's update arrives
  @state()
  protected _waiting: string | null = null;

  // A card to open beside the waiting line (a change already waiting for signatures); while there is one the card stays
  // open when its job completes, so the link can be followed
  @state()
  protected _waitingLink: string | null = null;

  // Why a button's change of amounts wasn't started (a Raise operating target card is open), with the card to open when
  // it is to be claimed there; the card stays open and its buttons stay
  @state()
  protected _blocked: { text: string; link: string | null } | null = null;

  @state()
  protected _people: Person[] = [];

  // Whether the row has been read (or couldn't be): until then a null row means "not read yet"
  @state()
  protected _rowLoaded = false;

  // Closes the card when its job completes even with no waiting line (an action whose card just closes)
  protected _closeWhenComplete = false;

  private __closed = false;

  async connectedCallback() {
    await super.connectedCallback();
    const job = this._job;
    if (!job) return;
    await this._loadRow();
    this._subscriptions.subscribe(`job.${job.id}.*`, () => {
      const updated = this._db?.get(`job.${job.id}`) as Job | undefined;
      if (updated) this._jobUpdated(updated);
    });
    this._subscriptions.subscribe('plAutoPayment.*', () => {
      void this._loadRow();
    });
  }

  protected get _me(): string | null {
    return this._directory?.me?.id ?? null;
  }

  protected get _claimed(): boolean {
    return !!this._job && !isUnclaimed(this._job);
  }

  protected get _completed(): boolean {
    return this._job?.status === 'COMPLETE' || this._job?.status === 'CANCELLED';
  }

  protected _jobUpdated(job: Job) {
    this._job = job;
    this.requestUpdate();
    // Completed by the server after this person's action: the card closes (once)
    const acted = this._waiting !== null || this._closeWhenComplete;
    if (job.status === 'COMPLETE' && acted && this._waitingLink === null && !this.__closed) {
      this.__closed = true;
      this._jobComplete();
    }
  }

  // The company's automatic payments row, as a signatory reads it; null when it can't be read
  protected async _loadRow() {
    const companyId = this._job?.companyId;
    try {
      if (!companyId || !this._db) return;
      const found = await this._db.query('plAutoPayment', { companyId: { $eq: companyId } });
      const [row] = (found?.results ?? []) as unknown as AutoPaymentRow[];
      this._row = row ?? null;
    } catch (err) {
      logger.warn('Unable to read the automatic payments settings', err);
    } finally {
      this._rowLoaded = true;
    }
  }

  // A person of the company by id, read once and kept, for names on the card
  protected async _loadPerson(personId: string | null | undefined) {
    if (!personId || !this._db || this._people.some((p) => p.id === personId)) return;
    try {
      const person = (await this._db.getById('people', personId)) as Person | undefined;
      if (person) this._people = [...this._people, person];
    } catch (err) {
      logger.warn('Unable to read a person', err);
    }
  }

  protected _nameOf(personId: string | null | undefined): string | null {
    if (!personId) return null;
    const person = this._people.find((p) => p.id === personId);
    return person ? personDisplayName(person) : null;
  }

  protected async _claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this._busy) return;
    this._busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._me ?? undefined };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this._busy = false;
    }
  }

  /**
   * Runs an action the server completes the job on. `run` answers what the card waits for once the action has been
   * accepted (shown until the job's update arrives, which closes the card; the job is read again here too, in case it
   * already has), or null when it wasn't (the card stays as it is, with whatever `run` showed). A refusal is shown on the
   * card with the lambda's own message.
   */
  protected async _serverAction(run: () => Promise<string | null>, loading: string, fallback: string) {
    if (!this._job || this._busy) return;
    this._busy = true;
    this.clearError();
    this.loading(loading);
    try {
      const waiting = await run();
      if (waiting === null) return;
      this._waiting = waiting;
      const updated = await this._db?.getById<Job>('job', this._job.id);
      if (updated) this._jobUpdated(updated);
    } catch (err) {
      this.showError(lambdaRefusal(err, fallback));
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  /**
   * A button that starts a change as this card's child (#25 phase 6, G12 d): card-action records who pressed it and
   * completes the card's job straight away with the action's outcome, whose `workflow/create` node starts (or adopts) the
   * change for them; the card's workflow then waits for that change to end. The change's own job is then opened straight
   * away, with no message (the feed if it can't be found yet). When a change of amounts already waits
   * for signatures (card-action answers waiting; the child adopts it), the card says so with an Open link to the card the
   * person should open for it (their signing card, else the change's), instead of opening anything. While a Raise the
   * automatic payments operating target card is open, a change of amounts can't start from Limit reached or Change target
   * (card-action answers blocked and doesn't complete the card): the card says so with the server's line, and an Open link
   * to the Raise card when it is to be claimed there.
   */
  protected async _startChange(action: keyof typeof CHILD_NODE, loading: string, fallback: string) {
    const job = this._job;
    const token = this._auth?.token;
    if (!job || this._busy) return;
    this._busy = true;
    this.clearError();
    this._blocked = null;
    this.loading(loading);
    try {
      // A reopened card's earlier child is still named on its workflow until the engine names the new one
      const previous = await currentChildWorkflow(this._db, job.workflow?.id, CHILD_NODE[action]);
      const answer = await autopayCardAction(token, job.id, action, fallback);
      // A Raise operating target card is open: nothing started and this card stays as it is
      if (answer.blocked) {
        const blocked = answer.blocked;
        this._blocked = { text: blocked.message, link: blocked.holderName ? null : jobFeedPath(blocked.jobId) };
        return;
      }
      if (answer.waiting) {
        this._waitingLink = jobFeedPath(answer.openJobId);
        this._waiting = CHANGE_WAITING_TEXT.AMOUNTS;
        return;
      }
      const child = await waitForChildWorkflow(
        this._db,
        answer.workflowId ?? job.workflow?.id,
        CHILD_NODE[action],
        previous,
      );
      // Not found yet: the feed, where it appears a moment later (no message, as from My Account; copy walkthrough 9)
      viewPath((await waitForOpenJobPath(this._db, child)) ?? '/feed');
    } catch (err) {
      this.showError(lambdaRefusal(err, fallback));
    } finally {
      this.loaded();
      this._busy = false;
    }
  }

  protected _renderClaimButton(): TemplateResult {
    return html`
      <wa-button variant="brand" type="button" ?disabled=${this._busy} @click=${() => this._claim()}>Claim</wa-button>
    `;
  }

  protected _renderClaimNote(what: string): TemplateResult {
    return html`
      <div class="job-panel">
        <p>Claim this job to ${what}. Any public ledger signatory can claim it.</p>
      </div>
    `;
  }

  protected _renderWaiting(): TemplateResult | null {
    if (this._waiting === null) return null;
    return this.__renderLine(this._waiting, this._waitingLink);
  }

  // Why the last button's change wasn't started, or nothing
  protected _renderBlocked(): TemplateResult | typeof nothing {
    if (!this._blocked || this._waiting !== null || this._completed) return nothing;
    return this.__renderLine(this._blocked.text, this._blocked.link);
  }

  private __renderLine(text: string, link: string | null): TemplateResult {
    return html`
      <div class="job-panel" role="status">
        <p>
          ${text}
          ${link
            ? html`
                <a
                  href=${link}
                  @click=${(e: Event) => {
                    e.preventDefault();
                    viewPath(link);
                  }}
                >
                  Open
                </a>
              `
            : nothing}
        </p>
      </div>
    `;
  }
}
